import React, { FC, useEffect } from "react";
import { Stack, SxProps } from "@mui/material";

import {
  Callout,
  Headline,
  Image,
  Paragraph,
  PlainTable,
} from "@components/Content";
import {
  CalloutProps,
  HeadlineProps,
  HubspotFormProps,
  ImageProps,
  ManageCookiesProps,
  ParagraphProps,
  PlainTableProps,
  HubspotStyledFormProps,
  DataCollectionOptOutProps,
} from "@components/Content/types";
import HubspotStyledForm from "@src/components/Hubspot/StyledForm";
import HubspotGenericForm from "@src/components/Hubspot/GenericForm";
import ManageCookies from "@src/components/ManageCookies";
import DataCollectionOptout from "@src/components/DataCollectionOptout";

type PageSection =
  | HeadlineProps
  | ParagraphProps
  | CalloutProps
  | ImageProps
  | PlainTableProps
  | HubspotFormProps
  | HubspotStyledFormProps
  | ManageCookiesProps
  | DataCollectionOptOutProps;

interface Props {
  sections: Array<PageSection>;
  additionalStyles?: SxProps;
}

const CommonPage: FC<Props> = ({ sections, additionalStyles = {} }) => {
  useEffect(() => {
    if (window.location.hash) {
      const hash = window.location.hash.substring(1);
      setTimeout(() => {
        const target = document.getElementById(hash);
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, []);

  return (
    <Stack
      sx={{
        maxWidth: "1080px",
        margin: "0 auto",
        padding: { xs: "0 24px", md: "0" },
        ...additionalStyles,
      }}
    >
      {sections.map((section: PageSection, idx) => {
        const sectionKey = `page-section-${idx}`;
        if (section.type === "paragraph") {
          return (
            <Paragraph
              content={section.content}
              containerSx={section.containerStyle}
              textSx={section.textStyle}
              key={sectionKey}
            />
          );
        } else if (section.type === "hubspot-form") {
          return (
            <HubspotGenericForm
              region={section.region}
              portalId={section.portalId}
              formId={section.formId}
              key={sectionKey}
            />
          );
        } else if (section.type === "hubspot-styled-form") {
          return (
            <HubspotStyledForm
              region={section.region}
              portalId={section.portalId}
              formId={section.formId}
              key={sectionKey}
              subText={section.subText}
              headerText={section.headerText}
            />
          );
        } else if (section.type === "headline") {
          const link = section.slug ? section.slug : sectionKey;
          return (
            <Headline
              content={section.content}
              variant={section.variant}
              link={link}
              key={sectionKey}
            />
          );
        } else if (section.type === "callout") {
          return <Callout content={section.content} key={sectionKey} />;
        } else if (section.type === "image") {
          return (
            <Image
              url={section.url}
              width={section.width}
              height={section.height}
              caption={section.caption}
              key={sectionKey}
            />
          );
        } else if (section.type === "table") {
          return <PlainTable content={section.content} key={sectionKey} />;
        } else if (section.type === "manage-cookies") {
          return <ManageCookies key={sectionKey} />;
        } else if (section.type === "data-collection-optout") {
          return <DataCollectionOptout key={sectionKey} />;
        }
      })}
    </Stack>
  );
};

export default CommonPage;
